import { useState } from "react";
import { useAccount, useReadContract, useWriteContract, useWatchContractEvent } from "wagmi";
import { Crown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { KING_OF_HILL_ABI, KING_OF_HILL_ADDRESS } from "./contractConfig";
import { parseEther } from "viem";
import { CurrentKingInfo } from "./components/CurrentKingInfo";
import { StakeInput } from "./components/StakeInput";
import { WithdrawButton } from "./components/WithdrawButton";
import { Leaderboard } from "./components/Leaderboard";
import { polygon } from "viem/chains";
import Header from "@/components/Header";

export const KingOfTheHill = () => {
  const [stake, setStake] = useState("");
  const { address, isConnected } = useAccount();
  const { toast } = useToast();
  const [isClaimLoading, setIsClaimLoading] = useState(false);
  const [isWithdrawLoading, setIsWithdrawLoading] = useState(false);

  // Read contract data
  const { data: currentKing } = useReadContract({
    address: KING_OF_HILL_ADDRESS,
    abi: KING_OF_HILL_ABI,
    functionName: "currentKing",
    chainId: polygon.id,
  });

  const { data: currentStake } = useReadContract({
    address: KING_OF_HILL_ADDRESS,
    abi: KING_OF_HILL_ABI,
    functionName: "currentStake",
    chainId: polygon.id,
  });

  const { data: rewardPool } = useReadContract({
    address: KING_OF_HILL_ADDRESS,
    abi: KING_OF_HILL_ABI,
    functionName: "rewardPool",
    chainId: polygon.id,
  });

  // Write contract functions
  const { writeContract: claimKing } = useWriteContract();
  const { writeContract: withdrawReward } = useWriteContract();

  // Watch for contract events
  useWatchContractEvent({
    address: KING_OF_HILL_ADDRESS,
    abi: KING_OF_HILL_ABI,
    eventName: 'KingClaimed',
    onLogs(logs) {
      console.log('New king claimed:', logs);
    },
  });

  const handleClaimKing = async () => {
    if (!isConnected) {
      toast({
        title: "Connect Wallet",
        description: "Please connect your wallet to claim the king position.",
        variant: "destructive",
      });
      return;
    }

    if (!stake || parseFloat(stake) <= 0) {
      toast({
        title: "Invalid Stake",
        description: "Please enter a valid stake amount.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsClaimLoading(true);
      await claimKing({
        address: KING_OF_HILL_ADDRESS,
        abi: KING_OF_HILL_ABI,
        functionName: 'claimKing',
        value: parseEther(stake),
        chain: polygon,
        account: address,
      });
      toast({
        title: "Transaction Submitted",
        description: "Your claim for the throne is being processed...",
      });
    } catch (error) {
      console.error("Error claiming king:", error);
      toast({
        title: "Transaction Failed",
        description: "Failed to claim the king position. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsClaimLoading(false);
    }
  };

  const handleWithdrawReward = async () => {
    if (!isConnected) {
      toast({
        title: "Connect Wallet",
        description: "Please connect your wallet to withdraw rewards.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsWithdrawLoading(true);
      await withdrawReward({
        address: KING_OF_HILL_ADDRESS,
        abi: KING_OF_HILL_ABI,
        functionName: 'withdrawReward',
        chain: polygon,
        account: address,
      });
      toast({
        title: "Transaction Submitted",
        description: "Your withdrawal request is being processed...",
      });
    } catch (error) {
      console.error("Error withdrawing reward:", error);
      toast({
        title: "Transaction Failed",
        description: "Failed to withdraw rewards. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsWithdrawLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8 pt-24">
        <div className="grid gap-8 md:grid-cols-2">
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center justify-center gap-2 text-2xl">
                <Crown className="h-6 w-6 text-yellow-500" />
                King of the Bots
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <CurrentKingInfo
                currentKing={currentKing}
                currentStake={currentStake}
                rewardPool={rewardPool}
              />

              <div className="space-y-4">
                <StakeInput
                  stake={stake}
                  onStakeChange={setStake}
                  onClaimKing={handleClaimKing}
                  isLoading={isClaimLoading}
                  isConnected={isConnected}
                />
                
                {currentKing === address && (
                  <WithdrawButton
                    onWithdraw={handleWithdrawReward}
                    isLoading={isWithdrawLoading}
                  />
                )}
                
                {!isConnected && (
                  <p className="text-sm text-muted-foreground text-center">
                    Connect your wallet to participate
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
          
          <Leaderboard />
        </div>
      </div>
    </div>
  );
};

export default KingOfTheHill;
